import { RectangleStackIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/16/solid';
import { formatShortScanDate } from '../formatScanDate';
import {
    getMarkerTone,
    getScanShape,
    isComfortScan,
    scanDecision,
    scanTitle
} from '../scanInstructionTone';
import { scanTakenAt } from '../scanTakenAt';
import { CIRCLE_SIZE, CIRCLE_Y, SELECTED_CIRCLE_SIZE } from './geometry';
import type { TimelineScan } from './scanScale';

interface ScanMarkerProps {
    id: string;
    point: TimelineScan;
    onSelect: (scanId: number) => void;
    /** False on a case with no aligners: number the scans instead of the aligners. */
    tracksAligners?: boolean;
}

function buildLabel(
    { scan, isLatest, displaced, sequence }: TimelineScan,
    tracksAligners: boolean
): string {
    const date = formatShortScanDate(scanTakenAt(scan));
    const identity = isComfortScan(scan)
        ? `Comfort scan, ${date}`
        : `${scanTitle(scan, tracksAligners, sequence)} · ${date}`;
    const late = scan.late ? ` · late${scan.daysLate ? ` by ${scan.daysLate}d` : ''}` : '';
    const latest = isLatest ? ' · latest scan' : '';
    // Says so plainly, because a nudged marker no longer sits on its own date.
    const nudged = displaced
        ? ' · nudged clear of a neighbouring scan, zoom in for its exact date'
        : '';
    return `${identity} · ${scan.status}${late}${latest}${nudged}`;
}

// A photo stack with a plus badge: an extra photo set, off the aligner schedule.
function ComfortScanGlyph({ isSelected }: { isSelected: boolean }) {
    return (
        <span aria-hidden className="relative flex">
            <RectangleStackIcon className={isSelected ? 'h-4 w-4' : 'h-3 w-3'} />
            <PlusIcon
                className={`absolute -right-1 -bottom-1 rounded-full bg-sky-600 text-white ${
                    isSelected ? 'h-2.5 w-2.5' : 'h-2 w-2'
                }`}
            />
        </span>
    );
}

function ScanMarker({ id, point, onSelect, tracksAligners = true }: ScanMarkerProps) {
    const { scan, isSelected, x, sequence } = point;
    const label = buildLabel(point, tracksAligners);
    const tone = getMarkerTone(scan, tracksAligners);
    const shape = getScanShape(tracksAligners ? scanDecision(scan) : null);
    const size = (isSelected ? SELECTED_CIRCLE_SIZE : CIRCLE_SIZE) * shape.scale;

    return (
        <button
            type="button"
            id={id}
            role="option"
            aria-selected={isSelected}
            tabIndex={-1}
            onClick={() => onSelect(scan.scanId)}
            aria-label={label}
            title={label}
            className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center border-[1.5px] tabular-nums leading-none transition-colors duration-200 ${shape.box} ${
                isSelected
                    ? `text-xs font-bold ${tone.selected}`
                    : `bg-white text-[11px] font-semibold text-gray-700 hover:bg-gray-50 ${tone.border}`
            }`}
            style={{
                top: CIRCLE_Y,
                left: x,
                width: size,
                height: size,
                zIndex: isSelected ? 20 : 10
            }}
        >
            {isComfortScan(scan) ? (
                <ComfortScanGlyph isSelected={isSelected} />
            ) : (
                <span className={shape.label}>
                    {tracksAligners ? (scan.alignerIndex ?? '?') : sequence}
                </span>
            )}
        </button>
    );
}

export { ScanMarker };
